<script setup lang="ts">
import { computed } from 'vue'
import { usePersonalAccountDataState } from '../../utilities/account/useAccountDataPersonal'

import { BriefcaseIcon, UserIcon } from '@heroicons/vue/outline'
import { getAccountLink } from '../../utilities/useLinks'
import { formatCountry, formatDate } from '../../utilities/helpers'
import BaseButtonPrimary from '../base/BaseButtonPrimary.vue'
import { isNaturalProfile } from '../../utilities/investment-process/useUserProfileType'
import { useLegalAccountDataState } from '../../utilities/account/useAccountDataLegal'
import { i18n } from '../../localization'

const personalAccountState = usePersonalAccountDataState()
const legalAccountState = useLegalAccountDataState()

const propertyList = computed<Array<{ label: string; value: string }>>(() =>
    isNaturalProfile()
        ? [
              {
                  label: i18n.global.t('account.email_address'),
                  value: personalAccountState.naturalProfile.email,
              },
              {
                  label: i18n.global.t('account.date_of_birth'),
                  value: personalAccountState.naturalProfile.date_of_birth
                      ? formatDate(personalAccountState.naturalProfile.date_of_birth)
                      : '',
              },
          ]
        : [
              {
                  label: i18n.global.t('account.tax_number'),
                  value: legalAccountState.legalProfile.tax_number,
              },
          ]
)
</script>

<template>
    <div class="bg-white px-4 py-5 shadow sm:rounded-lg sm:px-6">
        <div class="flex items-center space-x-4">
            <div class="flex-shrink-0">
                <div class="relative">
                    <div class="flex items-center justify-center rounded-full border-2 border-gray-300 bg-white p-3">
                        <UserIcon v-if="isNaturalProfile()" class="border-gray-150 w-10" />
                        <BriefcaseIcon v-else class="border-gray-150 w-6" />
                    </div>
                </div>
            </div>
            <div>
                <h1 class="text-2xl font-bold text-gray-900">
                    <template v-if="isNaturalProfile()">
                        {{ personalAccountState.naturalProfile?.first_name }}
                        {{ personalAccountState.naturalProfile?.last_name }}
                    </template>
                    <template v-else>{{ legalAccountState.legalProfile?.company_name }}</template>
                </h1>
                <p class="text-sm font-medium text-gray-500">
                    <template v-if="isNaturalProfile()">
                        {{ personalAccountState.naturalProfile?.city }},
                        {{ formatCountry(personalAccountState.naturalProfile?.country) }}
                    </template>
                    <template v-else
                        >{{ legalAccountState.legalProfile?.address }},
                        {{ legalAccountState.legalProfile?.postal_code }}
                        {{ legalAccountState.legalProfile?.city }}</template
                    >
                </p>
            </div>
        </div>

        <!-- Profile Section -->

        <div class="mt-5 border-t border-gray-200 py-5">
            <dl class="grid grid-cols-1 gap-x-4 gap-y-8 md:grid-cols-2">
                <div v-for="prop in propertyList" :key="prop.label" class="col-span-2">
                    <dt class="text-sm font-medium text-gray-500">{{ prop.label }}</dt>
                    <dd class="mt-1 text-sm text-gray-900">
                        {{ prop.value }}
                    </dd>
                </div>
            </dl>
        </div>
        <BaseButtonPrimary class="mt-4 w-full" :href="getAccountLink()">{{
            $t('wallet.edit_profile')
        }}</BaseButtonPrimary>
    </div>
</template>
